<template>
  <v-container grid-list-xl fluid>
    <v-layout row wrap>
      <v-flex lg12>
        <v-card>
          <hpo-filter
            :orderbutton="true"
            :listStatus="listStatus"
            :orderSummary="orderSummary"
            :headers="headers"
            :viewName="viewName"
            v-on:applyFilter="applyFilter"
            v-on:quickFilter="quickFilter"
          ></hpo-filter>
          <div class="vehicle-view-toolbar">
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="viewMode" mandatory dense>
              <v-btn small value="card">
                <v-icon small left>fa-th-large</v-icon>
                Card
              </v-btn>
              <v-btn small value="table">
                <v-icon small left>fa-list</v-icon>
                Bảng
              </v-btn>
            </v-btn-toggle>
          </div>
          <v-data-table
            v-if="viewMode === 'table'"
            :headers="headers"
            :items="items"
            :options.sync="pagination"
            :server-items-length="total_rows"
            :footer-props="items_per_page"
            :must-sort="true"
            :loading="loading"
            :mobile-breakpoint="100"
          >
            <template v-slot:item="{ item }">
              <tr :id="'r-'+item.ID">
                <td>{{ item.VehicleCode }}</td>
                <td>{{ item.LicensePlate }}</td>
                <td>
                  <v-chip small :color="getStatusColor(item.Status)" text-color="white">
                    {{ getStatusName(item.Status) }}
                  </v-chip>
                </td>
                <td class="text-right">{{ item.NumberOfPack }}</td>
                <td class="text-right">{{ item.NumberOfProduct }}</td>
                <td class="text-right">{{ item.TotalWeight }} kg</td>
                <td class="text-right">{{ item.TotalVolume }} m³</td>
                <td>{{ item.LoadDate | display_date('DD-MM-YYYY HH:mm') }}</td>
                <td>{{ item.ExportDate | display_date('DD-MM-YYYY HH:mm') }}</td>
                <td>{{ item.ImportDate | display_date('DD-MM-YYYY HH:mm') }}</td>
                <td class="text-center">
                  <v-tooltip top v-if="canEditStatus(item)">
                      <template v-slot:activator="{ on }">
                          <v-icon v-on="on" medium class="mr-2" @click="openStatusDialog(item)">fa-exchange-alt</v-icon>
                      </template>
                      <span>Chuyển trạng thái</span>
                  </v-tooltip>
                  <v-tooltip top>
                      <template v-slot:activator="{ on }">
                          <v-icon v-on="on" medium class="mr-2" @click="openHistoryDialog(item)">fa-history</v-icon>
                      </template>
                      <span>Xem nhật ký</span>
                  </v-tooltip>
                  <v-icon medium class="mr-2" @click="gotoDetail(item.ID)">
                    {{ (packageRole & 8) && item.Status == vehicleStatusEnum.DANG_XEP ? 'fa-edit' : 'fa-external-link-alt'}}
                  </v-icon>
                  <v-tooltip top v-if="(packageRole & 4) && item.Status == vehicleStatusEnum.DANG_XEP">
                      <template v-slot:activator="{ on }">
                          <v-icon v-on="on" medium @click="deleteItem(item.ID)">fa-trash-alt</v-icon>
                      </template>
                      <span>Xoá xe hàng</span>
                  </v-tooltip>
                </td>
              </tr>
            </template>
          </v-data-table>
          <div v-else class="vehicle-card-wrapper">
            <div v-if="loading" class="px-4 pb-4">
              <v-progress-linear indeterminate color="primary"></v-progress-linear>
            </div>
            <div v-else-if="!items || items.length === 0" class="vehicle-card-empty">
              Không có xe hàng phù hợp bộ lọc hiện tại.
            </div>
            <div v-else class="vehicle-card-grid">
              <v-card
                v-for="item in items"
                :key="item.ID"
                class="vehicle-card"
                :style="{ borderTopColor: getStepColor(item.Status) }"
              >
                <div class="vehicle-card__header">
                  <div>
                    <div class="vehicle-card__code">{{ item.VehicleCode || ('#' + item.ID) }}</div>
                    <div class="vehicle-card__plate">Biển số: {{ item.LicensePlate || 'Chưa cập nhật' }}</div>
                  </div>
                  <v-chip small :color="getStatusColor(item.Status)" :text-color="getStatusTextColor(item.Status)">
                    {{ getStatusName(item.Status) }}
                  </v-chip>
                </div>

                <div class="stepper-track">
                  <template v-for="(step, index) in vehicleStatusFlow">
                    <div :key="`step-${item.ID}-${step.value}`" class="stepper-track__item">
                      <span
                        class="stepper-dot"
                        :class="{
                          'stepper-dot--active': step.value === item.Status,
                          'stepper-dot--done': getStepIndex(item.Status) >= index + 1
                        }"
                        :style="{ backgroundColor: getStepperDotColor(item.Status, step.value) }"
                      ></span>
                      <span
                        v-if="index < vehicleStatusFlow.length - 1"
                        class="stepper-line"
                        :style="{ backgroundColor: getStepperLineColor(item.Status, step.value) }"
                      ></span>
                    </div>
                  </template>
                </div>

                <div class="vehicle-card__progress">
                  Tiến độ {{ getStepIndex(item.Status) }}/{{ vehicleStatusFlow.length }} · {{ getStatusName(item.Status) }}
                </div>

                <div class="vehicle-card__stats">
                  <div class="vehicle-card__stat">
                    <span class="vehicle-card__stat-label">Số kiện</span>
                    <strong class="vehicle-card__stat-value">{{ item.NumberOfProduct || 0 }}</strong>
                  </div>
                  <div class="vehicle-card__stat">
                    <span class="vehicle-card__stat-label">Tổng kg</span>
                    <strong class="vehicle-card__stat-value">{{ formatVehicleWeight(item.TotalWeight) }} kg</strong>
                  </div>
                  <div class="vehicle-card__stat">
                    <span class="vehicle-card__stat-label">Tổng CBM</span>
                    <strong class="vehicle-card__stat-value">{{ formatVehicleVolume(item.TotalVolume) }} m³</strong>
                  </div>
                  <div class="vehicle-card__stat">
                    <span class="vehicle-card__stat-label">Số đơn/bao</span>
                    <strong class="vehicle-card__stat-value">{{ item.NumberOfPack || 0 }}</strong>
                  </div>
                </div>

                <div class="vehicle-card__footer">
                  Tạo: {{ item.CreatedBy || 'Hệ thống' }} · {{ item.CreatedDate | display_date('DD/MM') }}
                </div>

                <div class="vehicle-card__actions">
                  <v-btn outlined small color="primary" @click="gotoDetail(item.ID)">
                    <v-icon small left>fa-eye</v-icon>
                    Xem kiện
                  </v-btn>
                  <v-btn outlined small color="secondary" @click="openHistoryDialog(item)">
                    <v-icon small left>fa-history</v-icon>
                    Nhật ký
                  </v-btn>
                  <v-btn
                    small
                    color="primary"
                    class="white--text"
                    :disabled="!canEditStatus(item) || isStatusUpdating(item.ID)"
                    @click="openStatusDialog(item)"
                  >
                    Đổi trạng thái
                    <v-icon small right>fa-caret-down</v-icon>
                  </v-btn>
                </div>
              </v-card>
            </div>

            <div class="vehicle-card-footer">
              <div class="vehicle-card-footer__meta">
                Hiển thị {{ paginationText }}
              </div>
              <div class="vehicle-card-footer__controls">
                <v-select
                  v-model="pagination.itemsPerPage"
                  :items="itemsPerPageOptions"
                  dense
                  outlined
                  hide-details
                  class="vehicle-card-footer__select"
                  label="Số dòng"
                ></v-select>
                <v-pagination
                  v-model="pagination.page"
                  :length="totalPages"
                  :total-visible="7"
                ></v-pagination>
              </div>
            </div>
          </div>
        </v-card>
      </v-flex>
      <vehicle-popup
        :readOnly="!((packageRole & 8) && currentItem.Status == vehicleStatusEnum.DANG_XEP)"
        :dialogVehicleDetail="dialogVehicleDetail"
        v-on:closePopup="closePopup"
      ></vehicle-popup>

      <v-dialog v-model="statusDialog" max-width="500px" persistent>
        <v-card class="status-change-card">
          <v-card-title class="headline d-flex justify-space-between align-center px-6 py-4">
            <span class="font-weight-bold">Chuyển trạng thái xe {{ statusDialogItem.VehicleCode }}</span>
            <v-btn icon @click="closeStatusDialog">
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="px-6 py-2">
            <div class="mb-4 d-flex align-center">
              <span class="grey--text text--darken-1 font-weight-medium mr-2">Hiện tại:</span>
              <v-chip small :color="getStatusColor(statusDialogItem.Status)" text-color="white">
                {{ getStatusName(statusDialogItem.Status) }}
              </v-chip>
            </div>
            <div class="mb-4">
              <div class="grey--text text--darken-1 font-weight-medium mb-1">Chuyển sang</div>
              <v-select
                :items="vehicleStatuses"
                v-model="newVehicleStatus"
                item-text="name"
                item-value="value"
                outlined
                dense
                hide-details
              ></v-select>
            </div>

            <!-- Alert warning box -->
            <v-alert
              v-if="newVehicleStatus !== null && newVehicleStatus !== statusDialogItem.Status"
              border="left"
              colored-border
              type="warning"
              icon="fa-exclamation-triangle"
              class="yellow lighten-4 black--text mt-3"
            >
              Bạn chuẩn bị chuyển <strong>{{ statusDialogItem.NumberOfProduct || 0 }} kiện</strong> từ "{{ getStatusName(statusDialogItem.Status) }}" -> "{{ getStatusName(newVehicleStatus) }}".
              <br />
              Toàn bộ kiện trên xe sẽ cập nhật theo.
            </v-alert>

            <v-text-field
              label="Ghi chú / lý do (tùy chọn)"
              v-model="statusChangeNote"
              outlined
              dense
              class="mt-4"
              hide-details
            ></v-text-field>

            <v-checkbox
              v-model="confirmStatusChange"
              label="Tôi xác nhận thay đổi này"
              hide-details
              class="mt-3"
            ></v-checkbox>
          </v-card-text>
          <v-card-actions class="pb-6 px-6 pt-4">
            <v-spacer></v-spacer>
            <v-btn color="grey lighten-2" class="text-capitalize px-4" @click="closeStatusDialog">Hủy</v-btn>
            <v-btn
              color="primary"
              class="text-capitalize px-4 white--text"
              :disabled="!confirmStatusChange || newVehicleStatus === statusDialogItem.Status"
              @click="submitStatusChange"
            >
              Xác nhận chuyển
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="historyDialog" max-width="900px">
        <v-card>
          <v-card-title class="headline d-flex justify-space-between align-center">
            <span>Nhật ký xe hàng {{ historyDialogItem.VehicleCode || ('#' + (historyDialogItem.ID || '')) }}</span>
            <v-btn icon @click="closeHistoryDialog">
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="historyHeaders"
              :items="changedHistories || []"
              :loading="historyLoading"
              no-data-text="Chưa có nhật ký thay đổi trạng thái xe hàng."
              :footer-props="{ 'items-per-page-options': [10, 20, 30, 50] }"
              :items-per-page="10"
              :mobile-breakpoint="100"
            >
              <template v-slot:item="{ item }">
                <tr>
                  <td>{{ item.CreatedDate | display_date('DD-MM-YYYY HH:mm:ss') }}</td>
                  <td class="font-weight-bold">{{ item.Username || 'Hệ thống' }}</td>
                  <td class="text-pre-line">{{ item.HistoryContent }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-layout>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import configs from "../../configs";
import vehicle_popup from "@/components/popups/vehicle";
import Filtering from "@/components/Filtering";
import { sendErrorNotice } from "@/commons/utils";
import _ from "lodash";

export default {
  components: {
    "hpo-filter": Filtering,
    "vehicle-popup": vehicle_popup,
  },
  data() {
    return {
      pagination: { ...configs.PAGINATION, sortBy: ["ID"]},
      items_per_page: _.cloneDeep(configs.ITEMS_PER_PAGE),
      viewName: "vehicle_list",
      filterConditions: [],
      quickFilterConditions: [],
      viewMode: "table",
      vehicleStatusEnum: configs.VEHICLE_STATUS_ENUM,
      dialogVehicleDetail: false,
      statusUpdatingIds: [],
      statusDialog: false,
      statusDialogItem: {},
      newVehicleStatus: null,
      statusChangeNote: "",
      confirmStatusChange: false,
      vehicleStatuses: configs.VEHICLE_STATUS,
      vehicleHistoryOrderType: 7,
      historyDialog: false,
      historyDialogItem: {},
      historyLoading: false,
      historyHeaders: [
        { text: "Thời gian", value: "CreatedDate", sortable: false },
        { text: "Người thao tác", value: "Username", sortable: false },
        { text: "Nội dung", value: "HistoryContent", sortable: false }
      ]
    };
  },
  watch: {
    pagination: {
      handler: function(val, oldVal) {
        let filterConditions = this.$store.state.commons.filterConditions;
        this.filterConditions = !_.isEmpty(filterConditions)
          ? filterConditions[this.viewName]
          : [];
        this.filter_data();
      },
      deep: true
    },
    viewMode(value) {
      this.syncViewModeToQuery(value);
    },
    '$route.query.vehicleView'(value) {
      const normalizedViewMode = this.normalizeViewMode(value);
      if (normalizedViewMode !== this.viewMode) {
        this.viewMode = normalizedViewMode;
      }
    },
    packageRole() {
      this.$root.$emit('REQUEST_ROOT_OPTION', {invisibleCreateButton: !(this.packageRole & 2)});
    }
  },
  computed: {
    ...mapState({
      items: state => state.vehicle.all.data,
      total_rows: state => state.vehicle.all.total,
      currentItem: state => state.vehicle.selected,
      loading: state => state.vehicle.loading,
      changedHistories: state => state.changedHistories.all.data
    }),
    packageRole() {
      // Use BigPackage's role as approved
      return this.$store.state.authentication.role.BigPackage;
    },
    adminRole() {
      return (this.$store.state.authentication.user.roletype & configs.ROLE_TYPE.Admin) == configs.ROLE_TYPE.Admin;
    },
    param() {
      return {
        pagination: this.pagination,
        filter: this.filterConditions,
        quickFilter: this.quickFilterConditions
      };
    },
    listStatus() {
      return configs.VEHICLE_STATUS;
    },
    orderSummary() {
      const summary = _.get(this.$store.state.vehicle, "all.summary");
      if (summary && !_.isEmpty(summary)) {
        return summary;
      }

      const statusCounts = _.countBy(this.items || [], "Status");
      return {
        TotalStatusAll: this.total_rows || 0,
        ..._.reduce(statusCounts, (result, total, status) => {
          result[`TotalStatus${status}`] = total;
          return result;
        }, {})
      };
    },
    vehicleStatusFlow() {
      return configs.VEHICLE_STATUS;
    },
    itemsPerPageOptions() {
      return _.get(this.items_per_page, "items-per-page-options", [20, 30, 40, 50, 100]);
    },
    totalPages() {
      const itemsPerPage = Number(this.pagination.itemsPerPage) || 20;
      const total = Number(this.total_rows) || 0;
      return Math.max(1, Math.ceil(total / itemsPerPage));
    },
    paginationText() {
      const total = Number(this.total_rows) || 0;
      if (total === 0) {
        return "0 kết quả";
      }
      const itemsPerPage = Number(this.pagination.itemsPerPage) || 20;
      const page = Number(this.pagination.page) || 1;
      const start = (page - 1) * itemsPerPage + 1;
      const end = Math.min(total, start + (this.items ? this.items.length : 0) - 1);
      return `${start}-${end} / ${total}`;
    },
    headers() {
      let dataType = configs.DATA_TYPE;
      return [
        {
          text: "Mã xe",
          value: "VehicleCode",
          filterable: true,
          quickSearch: true,
          dataType: dataType["String"]
        },
        {
          text: "Biển số xe",
          value: "LicensePlate",
          filterable: true,
          quickSearch: true,
          dataType: dataType["String"]
        },
        {
          text: "Trạng thái",
          value: "Status",
          filterable: true,
          values: configs.VEHICLE_STATUS,
          dataType: dataType["Enum"]
        },
        {
          text: "Số kiện",
          value: "NumberOfPack",
          align: "end",
          filterable: true,
          dataType: dataType["Number"]
        },
        {
          text: "Số đơn/bao",
          value: "NumberOfProduct",
          align: "end",
          filterable: true,
          dataType: dataType["Number"]
        },
        {
          text: "Tổng kg",
          value: "TotalWeight",
          align: "end",
          filterable: true,
          dataType: dataType["Number"]
        },
        {
          text: "Tổng m³",
          value: "TotalVolume",
          align: "end",
          filterable: true,
          dataType: dataType["Number"]
        },
        {
          text: "Ngày xếp xe",
          value: "LoadDate",
          filterable: true,
          dataType: dataType["Date"]
        },
        {
          text: "Ngày xuất",
          value: "ExportDate",
          filterable: true,
          dataType: dataType["Date"]
        },
        {
          text: "Ngày nhập",
          value: "ImportDate",
          filterable: true,
          dataType: dataType["Date"]
        },
        {
          text: "Thao tác",
          value: "",
          align: "center",
          sortable: false,
          filterable: false
        }
      ];
    }
  },
  methods: {
    filter_data() {
      this.$store.dispatch("vehicle/getList", this.param);
    },
    getStatusColor(status) {
      const match = _.find(configs.VEHICLE_STATUS, { value: status });
      return match ? match.color : "grey";
    },
    getStatusName(status) {
      const match = _.find(configs.VEHICLE_STATUS, { value: status });
      return match ? match.name : "Không xác định";
    },
    getStatusTextColor(status) {
      const match = _.find(configs.VEHICLE_STATUS, { value: status });
      return match ? (match.textColor || "white") : "white";
    },
    getStepIndex(status) {
      const foundIndex = _.findIndex(this.vehicleStatusFlow, { value: status });
      return foundIndex >= 0 ? foundIndex + 1 : 1;
    },
    getStepColor(stepValue) {
      const match = _.find(this.vehicleStatusFlow, { value: stepValue });
      const stepColors = {
        orange: "#fb8c00",
        blue: "#1e88e5",
        indigo: "#3949ab",
        "cyan darken-2": "#00acc1",
        "deep-purple": "#5e35b1",
        green: "#43a047"
      };
      return stepColors[match ? match.color : ""] || "#90a4ae";
    },
    getStepperDotColor(currentStatus, stepValue) {
      return this.getStepIndex(currentStatus) >= this.getStepIndex(stepValue)
        ? this.getStepColor(stepValue)
        : "#d7dde5";
    },
    getStepperLineColor(currentStatus, stepValue) {
      return this.getStepIndex(currentStatus) > this.getStepIndex(stepValue)
        ? this.getStepColor(stepValue)
        : "#d7dde5";
    },
    formatVehicleWeight(value) {
      const numberValue = Number(value) || 0;
      return numberValue.toLocaleString("vi-VN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    formatVehicleVolume(value) {
      const numberValue = Number(value) || 0;
      return numberValue.toLocaleString("vi-VN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    normalizeViewMode(value) {
      return value === "card" ? "card" : "table";
    },
    syncViewModeToQuery(value) {
      const nextViewMode = this.normalizeViewMode(value);
      const currentViewMode = this.normalizeViewMode(this.$route.query.vehicleView);
      if (currentViewMode === nextViewMode) {
        return;
      }
      this.$router.replace({
        query: {
          ...this.$route.query,
          vehicleView: nextViewMode
        }
      });
    },
    canEditStatus() {
      return (this.packageRole & 8) === 8;
    },
    getStatusOptions(item) {
      return _.map(configs.VEHICLE_STATUS, status => ({
        ...status,
        disabled: this.isStatusOptionDisabled(item, status.value)
      }));
    },
    isStatusOptionDisabled(item, statusValue) {
      if (statusValue === this.vehicleStatusEnum.DANG_XEP && item.Status !== this.vehicleStatusEnum.DANG_XEP && !this.adminRole) {
        return true;
      }
      return false;
    },
    isStatusUpdating(id) {
      return this.statusUpdatingIds.includes(id);
    },
    updateVehicleStatus(item, status) {
      if (status === item.Status) {
        return;
      }
      if (this.isStatusOptionDisabled(item, status)) {
        sendErrorNotice("Bạn không có quyền chuyển xe hàng về trạng thái Đang xếp.");
        return;
      }
      this.statusUpdatingIds = _.uniq([...this.statusUpdatingIds, item.ID]);
      this.$store.dispatch("vehicle/setDetail", {
        data: {
          ID: item.ID,
          Status: status
        },
        id: item.ID,
        param: this.param
      });
      setTimeout(() => {
        this.statusUpdatingIds = _.without(this.statusUpdatingIds, item.ID);
      }, 500);
    },
    exportVehicle(id, code) {
      window.getApp.$emit("REQUEST_CONFIRM_ACTION", 'ExportVehicle', id, `Bạn có chắc chắn xuất kho xe hàng ${code} không?`);
    },
    undoExportVehicle(id, code) {
      window.getApp.$emit("REQUEST_CONFIRM_ACTION", 'UnExportVehicle', id, `Bạn có chắc chắn huỷ xuất kho xe hàng ${code} không?`);
    },
    openStatusDialog(item) {
      this.statusDialogItem = item;
      this.newVehicleStatus = item.Status;
      this.statusChangeNote = "";
      this.confirmStatusChange = false;
      this.statusDialog = true;
    },
    closeStatusDialog() {
      this.statusDialog = false;
      this.statusDialogItem = {};
      this.newVehicleStatus = null;
      this.statusChangeNote = "";
      this.confirmStatusChange = false;
    },
    submitStatusChange() {
      if (!this.confirmStatusChange || this.newVehicleStatus === this.statusDialogItem.Status) {
        return;
      }
      this.statusUpdatingIds = _.uniq([...this.statusUpdatingIds, this.statusDialogItem.ID]);
      this.$store.dispatch("vehicle/setDetail", {
        data: {
          ID: this.statusDialogItem.ID,
          Status: this.newVehicleStatus,
          Note: this.statusChangeNote
        },
        id: this.statusDialogItem.ID,
        param: this.param
      });
      setTimeout(() => {
        this.statusUpdatingIds = _.without(this.statusUpdatingIds, this.statusDialogItem.ID);
      }, 500);
      this.closeStatusDialog();
    },
    openHistoryDialog(item) {
      this.historyDialogItem = item;
      this.historyDialog = true;
      this.fetchVehicleHistories(item.ID);
    },
    closeHistoryDialog() {
      this.historyDialog = false;
      this.historyDialogItem = {};
      this.historyLoading = false;
    },
    fetchVehicleHistories(vehicleId) {
      if (!vehicleId) return;
      this.historyLoading = true;
      this.$store.dispatch("changedHistories/getList", {
        orderId: vehicleId,
        orderType: this.vehicleHistoryOrderType
      });
      setTimeout(() => {
        this.historyLoading = false;
      }, 300);
    },
    gotoDetail(id) {
      var param = {
        pagination: {
          ...configs.PAGINATION,
          itemsPerPage: 100000
        }
      };
      this.$store.dispatch("vehicle/getDetail", {'id': id, 'param': param});
      setTimeout(() => {this.dialogVehicleDetail = true}, 200);
    },
    deleteItem(id) {
      if(this.packageRole & 4) {
        window.getApp.$emit("REQUEST_DELETE_DATA", id);
      }
      else {
        sendErrorNotice('Bạn không có quyền xóa xe hàng.');
      }
    },
    applyFilter(filterConditions) {
      let sortBy = this.pagination.sortBy;
      let sortDesc = this.pagination.sortDesc;
      this.pagination = configs.PAGINATION;
      this.pagination.sortBy = sortBy;
      this.pagination.sortDesc = sortDesc;
      this.filterConditions = filterConditions;
      this.filter_data();
    },
    quickFilter(filterConditions) {
      this.quickFilterConditions = filterConditions;
      this.filter_data();
    },
    createData() {
      if(this.packageRole & 2) {
        this.$store.commit("vehicle/setDetail", {
          data: {
            ID: 0,
            VehicleCode: '',
            LicensePlate: '',
            Status: 0,
            NumberOfPack: 0,
            NumberOfProduct: 0,
            TotalWeight: 0.0,
            TotalVolume: 0.0,
            Orders: [],
            OrderIDs: [],
            AvailableOrders: [],
            BigPackages: [],
            BigPackageIDs: [],
            AvailableBigPackages: [],
            SmallPackages: []
          }
        });
        this.dialogVehicleDetail = true;
      }
      else {
        sendErrorNotice('Bạn không có quyền thêm mới xe hàng.');
      }
    },
    saveItem(item) {
      this.$store.dispatch("vehicle/setDetail", {
        data: item,
        id: item.ID,
        param: this.param
      });
    },
    closePopup() {
      this.dialogVehicleDetail = false;
      setTimeout(this.filter_data, 200);
    }
  },
  mounted() {
    this.viewMode = this.normalizeViewMode(this.$route.query.vehicleView);
    this.syncViewModeToQuery(this.viewMode);
    this.$root.$emit('REQUEST_ROOT_OPTION', {invisibleCreateButton: !(this.packageRole & 2)});
    this.$root.$on("REQUEST_CREATE_DATA", this.createData);
    this.$root.$on("REQUEST_REFRESH_DATA", this.filter_data);
    this.$root.$on("CONFIRMED_REMOVE_DATA", id => {
      this.$store.dispatch("vehicle/removeDetail", id);
      setTimeout(this.filter_data, 200);
    });
    this.$root.$on("CONFIRMED_ACTION", ({action, id}) => {
      if(action == 'ExportVehicle') {
        this.saveItem({
          ID: id,
          Status: configs.VEHICLE_STATUS_ENUM.DA_XUAT_KHO
        });
      }
      else if(action == 'UnExportVehicle') {
        this.saveItem({
          ID: id,
          Status: configs.VEHICLE_STATUS_ENUM.DANG_XEP
        });
      }
    });
  },
  beforeDestroy() {
    this.$root.$off("REQUEST_CREATE_DATA", this.createData);
    this.$root.$off("REQUEST_REFRESH_DATA", this.filter_data);
    this.$root.$off("CONFIRMED_REMOVE_DATA");
    this.$root.$off("CONFIRMED_ACTION");
  }
};
</script>

<style scoped>
.vehicle-view-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 16px 12px;
}

.vehicle-card-wrapper {
  padding: 0 16px 16px;
}

.vehicle-card-empty {
  padding: 48px 16px;
  text-align: center;
  color: #607080;
}

.vehicle-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.vehicle-card {
  border-top: 4px solid #90a4ae;
  border-radius: 14px;
  padding: 16px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.vehicle-card:hover {
  box-shadow: 0 10px 24px rgba(27, 39, 51, 0.1);
  transform: translateY(-1px);
}

.vehicle-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.vehicle-card__code {
  font-size: 18px;
  font-weight: 700;
  color: #1b2733;
}

.vehicle-card__plate {
  margin-top: 4px;
  font-size: 13px;
  color: #6b7a8c;
}

.stepper-track {
  display: flex;
  align-items: center;
  margin-top: 16px;
}

.stepper-track__item {
  display: flex;
  align-items: center;
  flex: 1;
}

.stepper-dot {
  width: 10px;
  height: 10px;
  min-width: 10px;
  border-radius: 50%;
  background: #d7dde5;
  transition: all 0.2s ease;
}

.stepper-dot--done {
  transform: scale(1.02);
}

.stepper-dot--active {
  box-shadow: 0 0 0 4px rgba(31, 111, 235, 0.16);
}

.stepper-line {
  height: 2px;
  flex: 1;
  margin: 0 6px;
  background: #d7dde5;
}

.vehicle-card__progress {
  margin-top: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #4f5f70;
}

.vehicle-card__stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 16px;
}

.vehicle-card__stat {
  border: 1px solid #e3e6eb;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fafbfc;
}

.vehicle-card__stat-label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  color: #708090;
}

.vehicle-card__stat-value {
  font-size: 16px;
  color: #1b2733;
}

.vehicle-card__footer {
  margin-top: 14px;
  font-size: 13px;
  color: #6b7a8c;
}

.vehicle-card__actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
  flex-wrap: wrap;
}

.vehicle-card__actions .v-btn {
  flex: 1;
  font-size: 8px;
}

.vehicle-card__actions .v-btn i {
  font-size: 12px !important;
}

.vehicle-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 18px;
}

.vehicle-card-footer__meta {
  color: #607080;
  font-size: 13px;
}

.vehicle-card-footer__controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.vehicle-card-footer__select {
  width: 120px;
}

.vehicle-status-select {
  width: 130px;
}
.vehicle-status-select >>> .v-input__slot {
  padding: 0 4px !important;
}

@media (max-width: 959px) {
  .vehicle-card-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }

  .vehicle-card-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .vehicle-card-footer__controls {
    flex-direction: column;
    align-items: stretch;
  }

  .vehicle-card-footer__select {
    width: 100%;
  }
}
</style>
